// File: frontend/src/utils/parseApiError.ts
import { TFunction } from "i18next";
import { parseServerDate } from "./dateUtils";
import { NetworkError, ServerError, RateLimitError } from "../services/apiClient";

/**
 * Parses backend error codes/messages and returns a user-friendly
 * localized string. Centralizes the error parsing logic that was
 * previously duplicated in LoginModal, SettingsPage, etc.
 *
 * Backend sends structured error codes (e.g. "WRONG_PASSWORD",
 * "FROZEN:2026-03-18T22:12:01"); this function maps them to i18n keys.
 *
 * fallbackKey is the i18n key used when nothing matches. It defaults to the login
 * wording because this helper started life in the login flow, but every caller
 * outside that flow should pass its own: an unmapped admin-panel rejection used to
 * surface to the moderator as "an error occurred during login", which described
 * neither what they did nor why it was refused.
 */
export function parseApiError(
  errorMessage: string,
  t: TFunction,
  fallbackKey = "login_error_general"
): string {
  // Frozen account — backend sends "FROZEN:2026-03-18T22:12:01.712618"
  // Java LocalDateTime uses up to 9 decimal places; JS Date only handles 3 (ms).
  if (errorMessage.startsWith("FROZEN:")) {
    try {
      // Truncate sub-second part to 3 decimal places so JS Date can parse it
      const raw = errorMessage.substring(7).replace(/(\.\d{3})\d+/, "$1");
      const unfreezeAt = parseServerDate(raw);
      if (isNaN(unfreezeAt.getTime())) {
        return t("login_error_frozen_generic");
      }
      const formatted = unfreezeAt.toLocaleString("tr-TR", {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      return t("login_error_frozen", { date: formatted });
    } catch {
      return t("login_error_frozen_generic");
    }
  }

  // Deletion grace period cooldown — backend sends
  //   "DELETION_COOLDOWN:2026-03-28T10:00:00|2026-04-10T10:00:00"
  // The first date is when login (and therefore cancelling) becomes possible again;
  // the second is when the account is permanently closed if nobody cancels. The
  // second part is optional so an older token still produces a sensible message.
  if (errorMessage.startsWith("DELETION_COOLDOWN:")) {
    try {
      const payload = errorMessage.substring(18);
      const [rawCancelable, rawScheduled] = payload.split("|");
      const cancelableAt = parseServerDate(rawCancelable.replace(/(\.\d{3})\d+/, "$1"));
      if (isNaN(cancelableAt.getTime())) {
        return t("login_error_deletion_cooldown_generic");
      }
      const dateFormat = {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      } as const;
      const formatted = cancelableAt.toLocaleString("tr-TR", dateFormat);

      // Without the deletion date the user only learns half of what they need,
      // so the two-date wording is used whenever the backend supplied it.
      if (rawScheduled) {
        const scheduledAt = parseServerDate(rawScheduled.replace(/(\.\d{3})\d+/, "$1"));
        if (!isNaN(scheduledAt.getTime())) {
          return t("login_error_deletion_cooldown_with_deadline", {
            date: formatted,
            deleteDate: scheduledAt.toLocaleString("tr-TR", dateFormat),
          });
        }
      }
      return t("login_error_deletion_cooldown", { date: formatted });
    } catch {
      return t("login_error_deletion_cooldown_generic");
    }
  }

  // Deletion cancellation cooldown — backend sends "COOLDOWN:2026-03-20T14:00:00"
  if (errorMessage.startsWith("COOLDOWN:")) {
    return t("account_cancel_cooldown_remaining", { time: errorMessage.substring(9) });
  }

  // ── Custom topic error codes ──
  // Exact-match mapping, checked BEFORE the generic keyword heuristics below —
  // otherwise e.g. SUGGESTION_ALREADY_REVIEWED would be caught by the "already"
  // branch and shown as an unrelated email error.
  if (
    errorMessage === "CUSTOM_TOPIC_NOT_FOUND" ||
    errorMessage === "CUSTOM_TOPIC_TITLE_EXISTS" ||
    errorMessage === "CUSTOM_TOPIC_SLUG_EXISTS" ||
    errorMessage === "CUSTOM_TOPIC_SUGGESTION_PENDING" ||
    // Rookies (çaylaklar) are not allowed to suggest topics
    errorMessage === "CUSTOM_TOPIC_SUGGESTION_ROOKIE_FORBIDDEN" ||
    errorMessage === "SUGGESTION_NOT_FOUND" ||
    errorMessage === "SUGGESTION_ALREADY_REVIEWED" ||
    // Nickaltı topics: title/slug are managed by the owning account and the
    // topic can never be hard-deleted from the admin panel
    errorMessage === "NICKALTI_TOPIC_PROTECTED"
  ) {
    return t(errorMessage);
  }

  // Topic suggestion rate limits — backend sends "CUSTOM_TOPIC_SUGGESTION_RATE_LIMIT:10"
  // and "CUSTOM_TOPIC_SUGGESTION_DAILY_LIMIT:500" (configured value as suffix)
  if (errorMessage.startsWith("CUSTOM_TOPIC_SUGGESTION_RATE_LIMIT")) {
    const seconds = errorMessage.split(":")[1] ?? "";
    return t("CUSTOM_TOPIC_SUGGESTION_RATE_LIMIT", { seconds });
  }
  if (errorMessage.startsWith("CUSTOM_TOPIC_SUGGESTION_DAILY_LIMIT")) {
    const limit = errorMessage.split(":")[1] ?? "";
    return t("CUSTOM_TOPIC_SUGGESTION_DAILY_LIMIT", { limit });
  }

  // Too many channels on a topic — backend sends "CUSTOM_TOPIC_CHANNEL_LIMIT:10"
  // (allowed maximum as suffix)
  if (errorMessage.startsWith("CUSTOM_TOPIC_CHANNEL_LIMIT")) {
    const max = errorMessage.split(":")[1] ?? "";
    return t("CUSTOM_TOPIC_CHANNEL_LIMIT", { max });
  }

  // Numeric system setting outside its allowed range — backend sends
  // "SETTING_OUT_OF_RANGE:topic.title.max_length:50:80". The raw setting key is
  // shown on purpose: it is what the admin sees in the settings table.
  if (errorMessage.startsWith("SETTING_OUT_OF_RANGE:")) {
    const [, key = "", min = "", max = ""] = errorMessage.split(":");
    return t("admin_settings_range_error_key", { key, min, max });
  }

  // ── Entry content validation errors (sent with a numeric suffix) ──

  // Entry too short — backend sends "ENTRY_TOO_SHORT:10"
  if (errorMessage.startsWith("ENTRY_TOO_SHORT:")) {
    const min = errorMessage.substring(16);
    return t("entry_error_too_short", { min });
  }

  // Entry too long — backend sends "ENTRY_TOO_LONG:4000"
  if (errorMessage.startsWith("ENTRY_TOO_LONG:")) {
    const max = errorMessage.substring(15);
    return t("entry_error_too_long", { max });
  }

  // Daily entry limit reached — backend sends "ENTRY_DAILY_LIMIT_REACHED:50"
  if (errorMessage.startsWith("ENTRY_DAILY_LIMIT_REACHED:")) {
    const limit = errorMessage.substring(26);
    return t("entry_error_daily_limit", { limit });
  }

  // Daily entry edit limit reached — backend sends "ENTRY_DAILY_EDIT_LIMIT_REACHED:20"
  // Checked before ENTRY_DAILY_LIMIT_REACHED would ever match: the two prefixes are
  // distinct, but keeping this block first documents that they are separate quotas.
  if (errorMessage.startsWith("ENTRY_DAILY_EDIT_LIMIT_REACHED:")) {
    const limit = errorMessage.substring(31);
    return t("entry_error_daily_edit_limit", { limit });
  }

  // Edit window expired — backend sends "ENTRY_EDIT_WINDOW_EXPIRED:60"
  if (errorMessage.startsWith("ENTRY_EDIT_WINDOW_EXPIRED:")) {
    const minutes = errorMessage.substring(26);
    return t("entry_error_edit_window_expired", { minutes });
  }

  // Draft limit reached — backend sends "DRAFT_LIMIT_REACHED:20"
  if (errorMessage.startsWith("DRAFT_LIMIT_REACHED:")) {
    const limit = errorMessage.substring(20);
    return t("draft_error_limit_reached", { limit });
  }

  // Account status blocks — backend sends these when login is rejected due to status.
  // A fixed-term suspension arrives as "ACCOUNT_SUSPENDED:2026-08-02T18:45:11.482",
  // an open-ended one as the bare code. Same shape as FROZEN: above, including the
  // sub-second truncation Java's LocalDateTime needs before JS Date can read it.
  if (errorMessage.startsWith("ACCOUNT_SUSPENDED:")) {
    try {
      const raw = errorMessage.substring(18).replace(/(\.\d{3})\d+/, "$1");
      const suspendedUntil = parseServerDate(raw);
      if (isNaN(suspendedUntil.getTime())) {
        return t("login_error_suspended");
      }
      const formatted = suspendedUntil.toLocaleString("tr-TR", {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      return t("login_error_suspended_until", { date: formatted });
    } catch {
      return t("login_error_suspended");
    }
  }
  // The bare code with no date suffix is what an open-ended suspension sends —
  // it is not a missing date, it is the absence of one, and saying so is the
  // whole point. The dated branch above still falls back to the generic wording
  // when a date was sent but could not be parsed.
  if (errorMessage === "ACCOUNT_SUSPENDED") {
    return t("login_error_suspended_indefinite");
  }
  // A ban carries the admin's reason as a suffix — "ACCOUNT_BANNED:spam" — the same
  // way ACCOUNT_SUSPENDED carries its end date. The reason is free text and may well
  // contain colons of its own, so everything after the FIRST colon is the reason.
  // Checked before the bare code below, which is what a ban with no recorded reason
  // still sends.
  if (errorMessage.startsWith("ACCOUNT_BANNED:")) {
    const reason = errorMessage.substring(15).trim();
    return reason
      ? t("login_error_banned_with_reason", { reason })
      : t("login_error_banned");
  }
  if (errorMessage === "ACCOUNT_BANNED") {
    return t("login_error_banned");
  }
  // An admin removal carries the moment it was applied as a suffix —
  // "ACCOUNT_DELETED:2026-08-01T10:18:44.221" — the same shape ACCOUNT_SUSPENDED
  // uses for its end date, including the sub-second truncation JS Date needs.
  // Checked before the bare code below, which is what a row with no recorded
  // status-change timestamp still sends.
  if (errorMessage.startsWith("ACCOUNT_DELETED:")) {
    try {
      const raw = errorMessage.substring(16).replace(/(\.\d{3})\d+/, "$1");
      const deletedAt = parseServerDate(raw);
      if (isNaN(deletedAt.getTime())) {
        return t("login_error_deleted");
      }
      const formatted = deletedAt.toLocaleString("tr-TR", {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
      return t("login_error_deleted_on", { date: formatted });
    } catch {
      return t("login_error_deleted");
    }
  }
  if (errorMessage === "ACCOUNT_DELETED") {
    return t("login_error_deleted");
  }
  // The user closed this account themselves — say so, rather than the generic
  // "deleted" wording that reads like someone else did it to them.
  if (errorMessage === "ACCOUNT_CLOSED_BY_USER") {
    return t("login_error_account_closed_by_user");
  }
  // An admin tried to bring a permanently closed account back. The row survives,
  // but its profile, media, messages and password are gone — a status flip cannot
  // undo that, and AuthService keeps refusing the login on closed_at.
  if (errorMessage === "ACCOUNT_CLOSED_NOT_REOPENABLE") {
    return t("admin_error_account_closed_not_reopenable");
  }
  if (errorMessage === "ACCOUNT_INACTIVE") {
    return t("login_error_inactive");
  }

  // User registered with email verification enabled but hasn't clicked the link yet.
  // Distinct from ACCOUNT_INACTIVE which is for long-term non-login inactivity.
  if (errorMessage === "EMAIL_NOT_VERIFIED") {
    return t("login_error_email_not_verified");
  }

  // Verification email requested again too soon — backend sends
  // "VERIFICATION_RESEND_COOLDOWN:142" (seconds left). Every other resend outcome
  // answers 200 on purpose, so this is the only code this flow can produce.
  if (errorMessage.startsWith("VERIFICATION_RESEND_COOLDOWN:")) {
    const seconds = errorMessage.substring(29);
    return t("verify_resend_cooldown", { seconds });
  }

  // ── Password rule violations ──
  // Checked BEFORE the keyword heuristics below, otherwise "PASSWORD_TOO_SHORT"
  // would never be reached: the lower-cased fallback matches nothing here, but
  // the exact codes must stay ahead of any future partial match on "password".
  // The configured limit is sent as the suffix so the message quotes the real
  // number instead of hardcoding it a second time on the frontend.
  if (errorMessage === "PASSWORD_REQUIRED") {
    return t("PASSWORD_REQUIRED");
  }
  if (errorMessage.startsWith("PASSWORD_TOO_SHORT:")) {
    return t("PASSWORD_TOO_SHORT", { min: errorMessage.substring(19) });
  }
  if (errorMessage.startsWith("PASSWORD_TOO_LONG:")) {
    return t("PASSWORD_TOO_LONG", { max: errorMessage.substring(18) });
  }

  // ── Admin panel authorization refusals ──
  // Checked BEFORE the keyword heuristics further down so none of them can be
  // swallowed by a partial match. Each code has its own i18n key, so the panel
  // says exactly which rule stopped the action.
  if (
    errorMessage === "NOT_ADMIN_OR_MODERATOR" ||
    errorMessage === "ADMIN_ONLY_ACTION" ||
    errorMessage === "MOD_CANNOT_TARGET_STAFF" ||
    errorMessage === "MOD_CANNOT_ASSIGN_STAFF_ROLE" ||
    errorMessage === "CANNOT_MODIFY_SELF" ||
    errorMessage === "CANNOT_CHANGE_OWN_ROLE" ||
    errorMessage === "CANNOT_TARGET_ADMIN"
  ) {
    return t(errorMessage);
  }

  // Exact error code matches
  if (errorMessage === "WRONG_PASSWORD") {
    return t("password_change_error_wrong");
  }
  if (errorMessage === "EMAIL_SAME_AS_CURRENT") {
    return t("email_change_error_same");
  }
  if (errorMessage === "EMAIL_ALREADY_EXISTS") {
    return t("email_change_error_exists");
  }
  if (errorMessage === "FREEZE_BLOCKED_BY_DELETION") {
    return t("account_freeze_blocked_by_deletion");
  }

  // Draft-related errors
  if (errorMessage === "DRAFT_NOT_FOUND") {
    return t("draft_error_not_found");
  }
  if (errorMessage === "DRAFT_NOT_OWNER") {
    return t("draft_error_not_owner");
  }
  if (errorMessage === "DRAFT_EMPTY_CONTENT") {
    return t("draft_error_empty_content");
  }

  // Custom topic (gündem): the author already wrote the topic's latest entry
  if (errorMessage === "CUSTOM_TOPIC_CONSECUTIVE_ENTRY") {
    return t("custom_topic_consecutive_entry");
  }

  // Album image unpublish errors
  if (errorMessage === "ENTRY_IMAGE_NOT_FOUND") {
    return t("lightbox_unpublish_error_not_found");
  }
  if (errorMessage === "ENTRY_IMAGE_REMOVE_WOULD_EMPTY") {
    return t("lightbox_unpublish_error_would_empty");
  }

  // Partial match fallbacks — handle legacy messages with includes()
  const lower = errorMessage.toLowerCase();

  if (lower.includes("wrong") || lower.includes("incorrect")) {
    return t("password_change_error_wrong");
  }
  if (lower.includes("email_already") || lower.includes("already")) {
    return t("email_change_error_exists");
  }
  if (lower.includes("email_same") || lower.includes("same")) {
    return t("email_change_error_same");
  }
  if (lower.includes("invalid") && (lower.includes("email") || lower.includes("password"))) {
    return t("login_error_invalid_credentials");
  }
  if (lower.includes("not found") || lower.includes("does not exist")) {
    return t("login_error_invalid_credentials");
  }

  // Topic locked — backend sends 423 with this error code
  if (errorMessage === "TOPIC_LOCKED") {
    return t("TOPIC_LOCKED");
  }

  // Catch-all — never expose raw system error strings to the user
  return t(fallbackKey);
}
/**
 * Front door for any error caught around an apiClient call.
 *
 * parseApiError above only understands backend error CODES; it assumes the
 * request actually reached the server. This wrapper handles the cases where
 * it did not, so the user gets "your connection dropped" instead of a
 * page-specific "could not load entries" that hides the real cause.
 *
 * Usage:  catch (err) { setError(resolveRequestError(err, t)); }
 *
 * fallbackKey is forwarded to parseApiError for unmapped backend codes.
 */
export function resolveRequestError(
  err: unknown,
  t: TFunction,
  fallbackKey = "login_error_general"
): string {
  // Never reached the server — connection, DNS, blocked request
  if (err instanceof NetworkError || (err as Error)?.name === "NetworkError") {
    return t("error_network_offline");
  }

  // Reached the server, server failed — retrying shortly usually works
  if (err instanceof ServerError || (err as Error)?.name === "ServerError") {
    return t("error_server_unavailable");
  }

  // Too fast — backend rate limiter already sends a human message
  if (err instanceof RateLimitError || (err as Error)?.name === "RateLimitError") {
    return (err as Error).message || t("error_rate_limited");
  }

  // Anything else is a backend error code — hand it to the code mapper
  const message = (err as Error)?.message ?? "";
  return parseApiError(message, t, fallbackKey);
}

/**
 * Translate a raw backend error code that arrives as the message body of a
 * failed send (WebSocket or REST), e.g. "MESSAGE_RECIPIENT_BANNED".
 *
 * Some codes carry a payload after the first colon — a fixed-term suspension
 * sends "MESSAGE_RECIPIENT_SUSPENDED:2026-08-10T12:00:00.123" — so the base
 * code is what gets looked up and the suffix is passed in as the {{date}}
 * interpolation. A code with no suffix is looked up as-is.
 *
 * Returns null when the code has no translation, so the caller can fall back to
 * its own generic wording instead of showing the raw code to the user.
 */
export function translateMessageErrorCode(
  rawCode: string,
  t: TFunction
): string | null {
  if (!rawCode) return null;

  const separator = rawCode.indexOf(":");
  const baseCode = separator === -1 ? rawCode : rawCode.substring(0, separator);
  const payload = separator === -1 ? null : rawCode.substring(separator + 1);

  /*
   * Numeric payload: one or more plain numbers, e.g. "MESSAGE_RATE_LIMIT:3:60".
   * Checked before the date branch below, because a value like "3:60" is not a
   * date and would only fall through it after a pointless parse attempt.
   *
   * The numbers are handed to the translation as {{max}} and {{seconds}}, in
   * that order, so a message can state the limit currently in force rather than
   * a hard-coded one.
   *
   * Deliberately NOT named "count": i18next treats that option as a plural
   * selector and would go looking for MESSAGE_RATE_LIMIT_one / _other keys that
   * do not exist.
   */
  if (payload && /^\d+(:\d+)*$/.test(payload)) {
    const [max, seconds] = payload.split(":");
    const withNumbers = t(baseCode, { max, seconds });
    return withNumbers !== baseCode ? withNumbers : null;
  }

  if (payload) {
    // Java's LocalDateTime prints more than three sub-second digits, which JS
    // Date cannot read — same truncation the login path already does.
    const parsed = parseServerDate(payload.replace(/(\.\d{3})\d+/, "$1"));
    if (!isNaN(parsed.getTime())) {
      const formatted = parsed.toLocaleString("tr-TR", {
        day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
      });
      const withDate = t(baseCode, { date: formatted });
      return withDate !== baseCode ? withDate : null;
    }
  }

  const translated = t(baseCode);
  return translated !== baseCode ? translated : null;
}